import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import {
  AddFlight,
  AddFlightComplete,
  CreateFlight,
  CreateFlightComplete,
  DeleteFlight,
  DeleteFlightComplete,
  EditFlight,
  EditFlightComplete,
  LoadItineraries,
  LoadItinerariesComplete,
} from './itinerary.actions'
import { ItineraryService } from './itinerary.service'
import { Itinerary } from '@tv3/store/itinerary/itinerary.model'
import { Flight } from '@tv3/models/itinerary/flight'
import moment from 'moment'
import * as R from 'ramda'
import { ActionFailed, asUTCEpoch } from '@tokeet-frontend/tv3-platform'
import { FlightForm } from '@tv3/interfaces/forms/flight.form'

@Injectable()
export class ItineraryEffects {
  loadItineraries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadItineraries),
      switchMap(({ inquiryId }) =>
        this.itineraries.all(inquiryId).pipe(
          map((itineraries) => LoadItinerariesComplete({ itineraries })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  createFlight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateFlight),
      map(({ inquiry, form }) => {
        return {
          flight: this.getFlightData(form),
          inquiry,
        }
      }),
      switchMap(({ flight, inquiry }) =>
        this.itineraries.create(inquiry, flight).pipe(
          map((itinerary) => CreateFlightComplete({ itinerary })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  addFlight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddFlight),
      map(({ inquiry, itinerary, form }) => {
        let newItinerary = new Itinerary(itinerary)
        let flight = this.getFlightData(form)
        newItinerary.flights = R.append(flight, newItinerary.flights)
        return newItinerary
      }),
      switchMap((itinerary) =>
        this.itineraries.update(itinerary, 'add').pipe(
          map((itinerary) => AddFlightComplete({ update: { id: itinerary.id, changes: itinerary } })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  editFlight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditFlight),
      map(({ itinerary, form }) => {
        let newItinerary = new Itinerary(itinerary)
        let flight = this.getFlightData(form)
        newItinerary.flights = R.filter((f: Flight) => f.flightNum !== flight.flightNum, newItinerary.flights)
        newItinerary.flights = R.append(flight, newItinerary.flights)
        return newItinerary
      }),
      switchMap((itinerary) =>
        this.itineraries.update(itinerary, 'edit').pipe(
          map((itinerary) => EditFlightComplete({ update: { id: itinerary.id, changes: itinerary } })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  deleteFlight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteFlight),
      map(({ itinerary, flight }) => {
        let newItinerary = new Itinerary(itinerary)
        newItinerary.flights = R.filter((f: Flight) => f.flightNum !== flight.flightNum, newItinerary.flights)
        return newItinerary
      }),
      switchMap((itinerary) =>
        this.itineraries.update(itinerary, 'delete').pipe(
          map((itinerary) => DeleteFlightComplete({ update: { id: itinerary.id, changes: itinerary } })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  private getFlightData(form: FlightForm) {
    return new Flight({
      airline: form.airline,
      flightNum: form.flightNum,
      timezone: form.timezone,
      departure: asUTCEpoch(form.departure),
      arrival: asUTCEpoch(form.arrival),
    })
  }

  constructor(private actions$: Actions, private itineraries: ItineraryService) {}
}
